import { View, Pressable, ActivityIndicator } from "react-native"
import { useLocalSearchParams } from "expo-router"
import { SafeAreaView } from "react-native-safe-area-context"
import { Controller, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"

import { Header, Text, TextInput, Button, MCIcon } from "@src/components"
import { Alert } from "@src/components/ui/confirm"
import { CustomerPicker } from "@src/components/customer-picker"
import { NotificationBadge } from "@src/components/notification-badge"
import { KeyboardAwareScrollView } from "@src/components/keyboard-aware-scroll-view"
import { createNotificationGroupInput } from "@white-labels/input"
import { trpc, sharedStyles, type RouterOutputs } from "@src/utils"
import { ar } from "@src/i18n"

type Group = RouterOutputs["notifications"]["groups"]["get"]

// Mounted only after the group query resolves: useForm captures the loaded
// name as its defaultValues on the very first render, so the field shows the
// current name and no reset is ever needed.
function GroupEditor({ group }: { group: Group }) {
  const utils = trpc.useUtils()
  const updateGroup = trpc.notifications.groups.update.useMutation()
  const addMembers = trpc.notifications.groups.addMembers.useMutation()
  const removeMembers = trpc.notifications.groups.removeMembers.useMutation()

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty },
  } = useForm({ resolver: zodResolver(createNotificationGroupInput), defaultValues: { name: group.name }, mode: "onChange" })

  function invalidate() {
    void utils.notifications.groups.get.invalidate({ id: group.id })
    void utils.notifications.groups.list.invalidate()
  }

  const onRename = handleSubmit((data) => {
    updateGroup.mutate(
      { id: group.id, name: data.name },
      {
        onSuccess: invalidate,
        onError: (e) => Alert.alert(ar.error, e.message),
      },
    )
  })

  function onAddMember(customerId?: number) {
    if (!customerId) return
    addMembers.mutate(
      { groupId: group.id, customerIds: [customerId] },
      {
        onSuccess: invalidate,
        onError: (e) => Alert.alert(ar.error, e.message),
      },
    )
  }

  function onRemoveMember(customerId: number) {
    removeMembers.mutate(
      { groupId: group.id, customerIds: [customerId] },
      {
        onSuccess: invalidate,
        onError: (e) => Alert.alert(ar.error, e.message),
      },
    )
  }

  const membersBusy = addMembers.isPending || removeMembers.isPending

  return (
    <KeyboardAwareScrollView className="w-full max-w-content mx-auto pt-2" contentContainerStyle={sharedStyles.contentContainer}>
      <Text className="font-bold mb-2">{ar.notificationGroupName}</Text>
      <Controller
        control={control}
        name="name"
        render={({ field: { onChange, value } }) => <TextInput placeholder={ar.notificationGroupName} value={value} onChangeText={onChange} />}
      />
      <View className="mt-3 mb-6">
        <Button onPress={onRename} loading={updateGroup.isPending} disabled={!isValid || !isDirty || updateGroup.isPending}>
          {ar.save}
        </Button>
      </View>

      <Text className="font-bold mb-2">
        {ar.notificationGroupMembers} ({group.members.length})
      </Text>
      <View className="mb-3">
        <CustomerPicker onChange={onAddMember} disabled={membersBusy} />
      </View>

      <View className="gap-1.5">
        {group.members.length === 0 && <Text className="text-gray-500 text-center py-4">{ar.notificationNoMembers}</Text>}
        {group.members.map((member) => (
          <View key={member.id} className="bg-white rounded border border-gray-100 shadow-sm px-3 py-2.5 flex-row items-center justify-between">
            <View className="flex-row items-center gap-2 flex-1">
              <NotificationBadge reachable={member.reachable} optedIn={member.optedIn} />
              <Text className="text-base text-gray-900">{member.name}</Text>
            </View>
            <Pressable onPress={() => onRemoveMember(member.id)} disabled={membersBusy} className="p-2 active:opacity-60" hitSlop={6}>
              <MCIcon name="delete" size={18} color="#EF4444" />
            </Pressable>
          </View>
        ))}
      </View>

      <SafeAreaView edges={["bottom"]} className="mt-4" />
    </KeyboardAwareScrollView>
  )
}

export default function NotificationGroupDetail() {
  const params = useLocalSearchParams<{ id: string }>()
  const id = Number.parseInt(params.id, 10) as number
  const group = trpc.notifications.groups.get.useQuery({ id })

  if (group.isLoading) {
    return (
      <>
        <Header title={ar.notificationEditGroup} />
        <ActivityIndicator className="mt-4" />
      </>
    )
  }

  if (group.isError || !group.data) {
    return (
      <>
        <Header title={ar.notificationEditGroup} />
        <Text className="text-gray-500 text-center py-8">{ar.notificationNoGroups}</Text>
      </>
    )
  }

  return (
    <>
      <Header title={ar.notificationEditGroup} />
      {/* key: a route-id change must never leave a stale form mounted */}
      <GroupEditor key={group.data.id} group={group.data} />
    </>
  )
}
